// Actions
import { DynamicImportFailureAction } from "@ms/uno-actions/lib/local/app/DynamicImportFailureAction";
// Loader
import { ActionCreator } from "@ms/uno-actioncreators/lib/local/actioncreators/ActionCreator";
import { createComponentProxy } from "../../utilities/loader/LoaderUtilities";
// Utilities
import { NotSupportedError } from "@ms/uno-errors/lib/local/errors";
import { getInitialRoute } from "../../utilities/AppBootstrapRouteUtils";
// Static action creators
import { PerformanceCollectionActionCreator } from "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/performanceCollection/PerformanceCollectionActionCreator";
import { RoutingActionCreator } from "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/routing/RoutingActionCreator";
import { UnoShellActionCreator } from "@ms/uno-actioncreators/lib/local/actioncreators/unoShell/UnoShellActionCreator";
import { DataPreFetchActionCreator } from "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/dataPreFetch/DataPreFetchActionCreator";
/** Factory class for action creator providers in Base app */
export class BaseActionCreatorFactory {
    // #endregion
    /**
     * Initialize the action creator factory
     * @param loggers Loggers for telemetry
     */
    constructor(loader, dispatcher, modules, stores, configProvider, loggers) {
        this.loader = loader;
        this.dispatcher = dispatcher;
        this.modules = modules;
        this.stores = stores;
        this.configProvider = configProvider;
        this.loggers = loggers;
        this.loadActivityAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Activity), () => import(
        /* webpackChunkName: "uno-activityac" */ "@ms/uno-actioncreators/lib/local/actioncreators/activity/ActivityActionCreator"), (bundle) => new bundle.ActivityActionCreator(this.dispatcher));
        this.loadAppContextAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.AppContext), () => import(
        /* webpackChunkName: "uno-appcontextac" */ "@ms/uno-actioncreators/lib/local/actioncreators/appContext/AppContextActionCreator"), (bundle) => new bundle.AppContextActionCreator(this.dispatcher, this.modules, this.loggers));
        this.loadBasicBucketAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.BasicBucket), () => import(
        /* webpackChunkName: "uno-basicbucketac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/bucket/BasicBucketActionCreator"), (bundle) => new bundle.BasicBucketActionCreator(this.stores, this.modules, this.loggers));
        this.loadBasicPlanAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.BasicPlan), () => import(
        /* webpackChunkName: "uno-basicplanac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/plan/BasicPlanActionCreator"), (bundle) => new bundle.BasicPlanActionCreator(this.modules, this.stores, this.dispatcher, this.loggers, this.configProvider));
        this.loadGoalAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Goal), () => import(
        /* webpackChunkName: "uno-goalac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/goal/GoalActionCreator"), (bundle) => new bundle.GoalActionCreator(this.modules, this.loggers));
        this.loadBasicTaskAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.BasicTask), () => import(
        /* webpackChunkName: "uno-basictaskac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/task/BasicTaskActionCreator"), (bundle) => new bundle.BasicTaskActionCreator(this.dispatcher, this.stores, this.modules, this.loggers, this.configProvider));
        this.loadExternalAgentAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.ExternalAgent), () => import(
        /* webpackChunkName: "uno-externalagentac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/externalAgent/ExternalAgentActionCreator"), (bundle) => new bundle.ExternalAgentActionCreator(this.dispatcher, this.stores, this.modules, this.loggers));
        this.loadCampaignCalloutViewAcProvider = () => this.loader.load(this.getAcBundleKey(ActionCreator.CampaignCalloutView), () => import(
        /* webpackChunkName: "uno-campaigncalloutviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/view/CampaignCalloutViewActionCreator"), (bundle) => new bundle.CampaignCalloutViewActionCreator(this.modules, this.stores, this.loggers, this.configProvider));
        this.loadCapabilitiesAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Capabilities), () => import(
        /* webpackChunkName: "uno-capabilitiesac" */ "@ms/uno-actioncreators/lib/local/actioncreators/capabilities/CapabilitiesActionCreator"), (bundle) => {
            const clientFlavor = this.configProvider().sessionMetaData.appMetadata.clientFlavor;
            const environment = this.configProvider().sessionMetaData.environment;
            const initialRoute = getInitialRoute(clientFlavor, environment);
            return new bundle.CapabilitiesActionCreator(this.dispatcher, this.stores, this.modules, this.loggers, initialRoute, this.configProvider);
        });
        this.loadChatAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Chat), () => import(/* webpackChunkName: "uno-chatac" */ "@ms/uno-actioncreators/lib/local/actioncreators/chat/ChatActionCreator"), (bundle) => new bundle.ChatActionCreator(this.stores, this.modules, this.loggers, this.configProvider));
        this.loadContainerAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Container), () => import(
        /* webpackChunkName: "uno-containerac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/container/ContainerActionCreator"), (bundle) => new bundle.ContainerActionCreator(this.modules, this.loggers));
        this.loadCreateTaskFromTeamsMessageAc = () => {
            throw new NotSupportedError(`CreateTaskFromTeamsMessageAc`);
        };
        this.loadDeltaSyncAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.DeltaSync), () => import(
        /* webpackChunkName: "uno-deltasyncac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/deltaSync/DeltaSyncActionCreator"), (bundle) => new bundle.DeltaSyncActionCreator(this.modules, this.stores, this.dispatcher, this.loggers, this.configProvider));
        this.loadDriveItemAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.DriveItem), () => import(
        /* webpackChunkName: "uno-driveitemac" */ "@ms/uno-actioncreators/lib/local/actioncreators/driveItem/DriveItemActionCreator"), (bundle) => new bundle.DriveItemActionCreator(this.modules, this.loggers));
        this.loadExportAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Export), () => import(
        /* webpackChunkName: "uno-excelexportac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/export/ExportActionCreator"), (bundle) => new bundle.ExportActionCreator(this.dispatcher, this.stores, this.modules, this.loggers));
        this.loadGroupsAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Groups), () => import(
        /* webpackChunkName: "uno-groupsac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/groups/GroupsActionCreator"), (bundle) => new bundle.GroupsActionCreator(this.dispatcher, this.stores, this.modules, this.loggers, this.configProvider));
        this.loadIntelligenceAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Intelligence), () => import(
        /* webpackChunkName: "uno-intelligenceac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/intelligence/IntelligenceActionCreator"), (bundle) => new bundle.IntelligenceActionCreator(this.modules));
        this.loadLinksAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Links), () => import(
        /* webpackChunkName: "uno-linksac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/links/LinksActionCreator"), (bundle) => new bundle.LinksActionCreator(this.modules, this.dispatcher, this.stores, this.loggers));
        this.loadModalAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Modal), () => import(
        /* webpackChunkName: "uno-modalac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/modal/ModalActionCreator"), (bundle) => new bundle.ModalActionCreator(this.dispatcher, this.stores, this.loggers, this.configProvider));
        this.loadMruAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Mru), () => import(/* webpackChunkName: "uno-mruac" */ "@ms/uno-actioncreators/lib/local/actioncreators/mru/MruActionCreator"), (bundle) => new bundle.MruActionCreator(this.modules, this.stores, this.loggers));
        this.loadNotificationAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Notification), () => import(
        /* webpackChunkName: "uno-notificationac" */ "@ms/uno-actioncreators/lib/local/actioncreators/notification/NotificationActionCreator"), (bundle) => new bundle.NotificationActionCreator(this.dispatcher, this.modules));
        this.loadOCVAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.OCV), () => import(/* webpackChunkName: "uno-ocvac" */ "@ms/uno-actioncreators/lib/local/actioncreators/OCV/OCVActionCreator"), (bundle) => new bundle.OCVActionCreator(this.modules, this.stores, this.loggers, this.configProvider));
        this.loadOperationTrackingAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.OperationTracking), () => import(
        /* webpackChunkName: "uno-operationtrackingac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/operationTracking/OperationTrackingActionCreator"), (bundle) => new bundle.OperationTrackingActionCreator(this.dispatcher, this.modules, this.stores, this.loggers, this.configProvider));
        this.loadOrgAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Org), () => import(/* webpackChunkName: "uno-orgac" */ "@ms/uno-actioncreators/lib/local/actioncreators/org/OrgActionCreator"), (bundle) => new bundle.OrgActionCreator(this.modules, this.loggers, this.configProvider));
        this.loadPlanConversionAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.PlanConversion), () => import(
        /* webpackChunkName: "uno-planconversionac" */ "@ms/uno-actioncreators/lib/local/actioncreators/upsell/PlanConversionActionCreator"), (bundle) => new bundle.PlanConversionActionCreator(this.configProvider, this.modules, this.stores, this.loggers));
        this.loadPortfolioAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Portfolio), () => import(
        /* webpackChunkName: "uno-portfolioac" */ "@ms/uno-actioncreators/lib/local/actioncreators/portfolio/PortfolioActionCreator"), (bundle) => new bundle.PortfolioActionCreator(this.dispatcher, this.modules, this.stores, this.loggers, this.configProvider));
        this.loadPublishingAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Publishing), () => import(
        /* webpackChunkName: "uno-publishingac" */ "@ms/uno-actioncreators/lib/local/actioncreators/publishing/PublishingActionCreator"), (bundle) => new bundle.PublishingActionCreator());
        this.loadPremiumPlanAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.PremiumPlan), () => import(
        /* webpackChunkName: "uno-premiumplanac" */ "@ms/uno-actioncreators/lib/local/actioncreators/project/premiumPlan/PremiumPlanActionCreator"), (bundle) => new bundle.PremiumPlanActionCreator(this.modules, this.stores, this.dispatcher, this.loggers, this.configProvider));
        this.loadProjectHomeResourceAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.ProjectHomeResource), () => import(
        /* webpackChunkName: "uno-projecthomeresourceac" */ "@ms/uno-actioncreators/lib/local/actioncreators/projectHome/ProjectHomeResourceActionCreator"), (bundle) => new bundle.ProjectHomeResourceActionCreator(this.dispatcher, this.modules, this.stores, this.configProvider, this.loggers));
        this.loadRecommendedPlansAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.RecommendedPlans), () => import(
        /* webpackChunkName: "uno-recommendedplansac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/recommendedPlan/RecommendedPlansActionCreator"), (bundle) => new bundle.RecommendedPlansActionCreator(this.dispatcher, this.modules, this.stores, this.configProvider, this.loggers));
        this.loadTeamsAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Teams), () => import(/* webpackChunkName: "uno-teamsac" */ "@ms/uno-actioncreators/lib/local/actioncreators/teams/TeamsActionCreator"), (bundle) => new bundle.TeamsActionCreator(this.modules, this.loggers));
        this.loadTeamsTabAc = () => {
            throw new NotSupportedError("TeamsTabAc");
        };
        this.loadTenantAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Tenant), () => import(/* webpackChunkName: "uno-tenantac" */ "@ms/uno-actioncreators/lib/local/actioncreators/tenant/TenantActionCreator"), (bundle) => new bundle.TenantActionCreator(this.modules, this.loggers));
        this.loadUpsellAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Upsell), () => import(/* webpackChunkName: "uno-upsellac" */ "@ms/uno-actioncreators/lib/local/actioncreators/upsell/UpsellActionCreator"), (bundle) => new bundle.UpsellActionCreator(this.dispatcher, this.configProvider, this.modules, this.stores, this.loggers));
        this.loadUserAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.User), () => import(
        /* webpackChunkName: "uno-userac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/user/UserActionCreator"), (bundle) => new bundle.UserActionCreator(this.dispatcher, this.modules, this.stores, this.loggers, this.configProvider));
        this.loadKeyboardHandlersAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.KeyboardHandlers), () => import(
        /* webpackChunkName: "uno-keyboardhandlersac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/keyboardHandlers/KeyboardHandlersActionCreator"), (bundle) => new bundle.KeyboardHandlersActionCreator(this.stores, this.loggers, this.configProvider));
        this.loadBasicPlanViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.BasicPlanView), () => import(
        /* webpackChunkName: "uno-basicplanviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/view/BasicPlanViewActionCreator"), (bundle) => new bundle.BasicPlanViewActionCreator(this.dispatcher, this.modules, this.stores, this.loggers, this.configProvider));
        this.loadCreatePortfolioViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.CreatePortfolioView), () => import(
        /* webpackChunkName: "uno-createportfolioviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/view/CreatePortfolioViewActionCreator"), (bundle) => new bundle.CreatePortfolioViewActionCreator(this.modules, this.loggers));
        this.loadCreatePlanViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.CreatePlanView), () => import(
        /* webpackChunkName: "uno-createplanviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/view/CreatePlanViewActionCreator"), (bundle) => new bundle.CreatePlanViewActionCreator(this.modules, this.loggers, this.configProvider));
        this.loadCopyPlanViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.CopyPlanView), () => import(
        /* webpackChunkName: "uno-copyplanviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/view/CopyPlanViewActionCreator"), (bundle) => new bundle.CopyPlanViewActionCreator(this.modules, this.loggers, this.configProvider));
        this.loadLeftNavViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.LeftNavView), () => import(
        /* webpackChunkName: "uno-leftnavviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/view/LeftNavViewActionCreator"), (bundle) => new bundle.LeftNavViewActionCreator(this.modules, this.stores, this.loggers, this.configProvider));
        this.loadMyDayViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.MyDayView), () => import(
        /* webpackChunkName: "uno-mydayviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/view/MyDayViewActionCreator"), (bundle) => new bundle.MyDayViewActionCreator(this.modules, this.loggers, this.configProvider));
        this.loadMyPlansViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.MyPlansView), () => import(
        /* webpackChunkName: "uno-myplansviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/view/MyPlansViewActionCreator"), (bundle) => new bundle.MyPlansViewActionCreator(this.modules, this.loggers));
        this.loadMyTasksViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.MyTasksView), () => import(
        /* webpackChunkName: "uno-mytasksviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/view/MyTasksViewActionCreator"), (bundle) => new bundle.MyTasksViewActionCreator(this.modules, this.stores, this.loggers));
        this.loadMyPortfoliosViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.MyPortfoliosView), () => import(
        /* webpackChunkName: "uno-myportfoliosviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/view/MyPortfoliosViewActionCreator"), (bundle) => new bundle.MyPortfoliosViewActionCreator(this.modules, this.loggers));
        this.loadUnoShellViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.UnoShellView), () => import(
        /* webpackChunkName: "uno-unoshellviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/view/UnoShellViewActionCreator"), (bundle) => {
            const clientFlavor = this.configProvider().sessionMetaData.appMetadata.clientFlavor;
            const environment = this.configProvider().sessionMetaData.environment;
            const initialRoute = getInitialRoute(clientFlavor, environment);
            return new bundle.UnoShellViewActionCreator(this.dispatcher, this.modules, this.stores, this.loggers, this.configProvider, initialRoute);
        });
        this.loadViewTransformsAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.ViewTransforms), () => import(
        /* webpackChunkName: "uno-viewtransformsac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/viewTransforms/ViewTransformsActionCreator"), (bundle) => new bundle.ViewTransformsActionCreator(this.dispatcher, this.modules));
        this.loadConvertPlanViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.ConvertPlanView), () => import(
        /* webpackChunkName: "uno-convertplanviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/view/ConvertPlanViewActionCreator"), (bundle) => new bundle.ConvertPlanViewActionCreator(this.modules, this.loggers));
        this.loadSharePlanViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.SharePlanView), () => import(
        /* webpackChunkName: "uno-shareplanviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/view/SharePlanViewActionCreator"), (bundle) => new bundle.SharePlanViewActionCreator(this.dispatcher, this.modules, this.stores, this.loggers, this.configProvider));
        this.loadCreateTeamsTabViewAc = () => {
            throw new NotSupportedError("CreateTeamsTabViewAc");
        };
        this.loadCampaignAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Campaign), () => import(
        /* webpackChunkName: "uno-campaignac" */ "@ms/uno-actioncreators/lib/local/actioncreators/campaign/CampaignActionCreator"), (bundle) => new bundle.CampaignActionCreator(this.dispatcher, this.modules, this.stores, this.loggers, this.configProvider));
        this.loadPlanAiAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.PlanAi), () => import(/* webpackChunkName: "uno-planaiac" */ "@ms/uno-actioncreators/lib/local/actioncreators/planAi/PlanAiActionCreator"), (bundle) => new bundle.PlanAiActionCreator(this.dispatcher, this.loggers, this.modules, this.stores));
        this.loadTemplatesAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.Template), () => import(
        /* webpackChunkName: "uno-templateac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/templates/TemplateActionCreator"), (bundle) => new bundle.TemplateActionCreator(this.modules, this.stores, this.dispatcher, this.loggers, this.configProvider));
        this.loadPublishingLeftNavViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.PublishingLeftNavView), () => import(
        /* webpackChunkName: "publishingleftnavviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/view/PublishingLeftNavViewActionCreator"), (bundle) => new bundle.PublishingLeftNavViewActionCreator(this.modules, this.loggers));
        this.loadCreateTaskViewAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.CreateTaskView), () => import(
        /* webpackChunkName: "uno-createtaskviewac" */ "@ms/uno-actioncreators/lib/local/actioncreators/legacyPlanner/view/CreateTaskViewActionCreator"), (bundle) => new bundle.CreateTaskViewActionCreator(this.modules, this.stores, this.loggers, this.configProvider));
        this.loadSuiteNavViewAc = () => {
            throw new NotSupportedError("SuiteNavViewAc");
        };
        this.loadNewsBarAc = () => this.loader.load(this.getAcBundleKey(ActionCreator.NewsBar), () => import(
        /* webpackChunkName: "uno-newsbarac" */ "@ms/uno-actioncreators/lib/local/actioncreators/newsBar/NewsBarActionCreator"), (bundle) => new bundle.NewsBarActionCreator(this.dispatcher));
        /** Map of all functions that load an action creator */
        this.actionCreatorLoaders = {
            [ActionCreator.Activity]: this.loadActivityAc,
            [ActionCreator.AppContext]: this.loadAppContextAc,
            [ActionCreator.BasicBucket]: this.loadBasicBucketAc,
            [ActionCreator.BasicPlan]: this.loadBasicPlanAc,
            [ActionCreator.BasicTask]: this.loadBasicTaskAc,
            [ActionCreator.DataPreFetch]: this.getDataPreFetchAcProvider().bind(this),
            [ActionCreator.Campaign]: this.loadCampaignAc,
            [ActionCreator.Capabilities]: this.loadCapabilitiesAc,
            [ActionCreator.Chat]: this.loadChatAc,
            [ActionCreator.Container]: this.loadContainerAc,
            [ActionCreator.CreateTaskFromTeamsMessage]: this.loadCreateTaskFromTeamsMessageAc,
            [ActionCreator.DeltaSync]: this.loadDeltaSyncAc,
            [ActionCreator.DriveItem]: this.loadDriveItemAc,
            [ActionCreator.Export]: this.loadExportAc,
            [ActionCreator.ExternalAgent]: this.loadExternalAgentAc,
            [ActionCreator.Goal]: this.loadGoalAc,
            [ActionCreator.Groups]: this.loadGroupsAc,
            [ActionCreator.Intelligence]: this.loadIntelligenceAc,
            [ActionCreator.Links]: this.loadLinksAc,
            [ActionCreator.Modal]: this.loadModalAc,
            [ActionCreator.Mru]: this.loadMruAc,
            [ActionCreator.NewsBar]: this.loadNewsBarAc,
            [ActionCreator.Notification]: this.loadNotificationAc,
            [ActionCreator.OCV]: this.loadOCVAc,
            [ActionCreator.OperationTracking]: this.loadOperationTrackingAc,
            [ActionCreator.Org]: this.loadOrgAc,
            [ActionCreator.PerformanceCollection]: this.getPerformanceCollectionAcProvider(),
            [ActionCreator.PlanAi]: this.loadPlanAiAc,
            [ActionCreator.PlanConversion]: this.loadPlanConversionAc,
            [ActionCreator.Portfolio]: this.loadPortfolioAc,
            [ActionCreator.Publishing]: this.loadPublishingAc,
            [ActionCreator.PremiumPlan]: this.loadPremiumPlanAc,
            [ActionCreator.ProjectHomeResource]: this.loadProjectHomeResourceAc,
            [ActionCreator.RecommendedPlans]: this.loadRecommendedPlansAc,
            [ActionCreator.Routing]: this.getRoutingAcProvider().bind(this),
            [ActionCreator.Teams]: this.loadTeamsAc,
            [ActionCreator.TeamsTab]: this.loadTeamsTabAc,
            [ActionCreator.Template]: this.loadTemplatesAc,
            [ActionCreator.Tenant]: this.loadTenantAc,
            [ActionCreator.UnoShell]: this.getUnoShellAcProvider().bind(this),
            [ActionCreator.Upsell]: this.loadUpsellAc,
            [ActionCreator.User]: this.loadUserAc,
            [ActionCreator.KeyboardHandlers]: this.loadKeyboardHandlersAc,
            [ActionCreator.ViewTransforms]: this.loadViewTransformsAc,
            [ActionCreator.BasicPlanView]: this.loadBasicPlanViewAc,
            [ActionCreator.CampaignCalloutView]: this.loadCampaignCalloutViewAcProvider,
            [ActionCreator.CreatePortfolioView]: this.loadCreatePortfolioViewAc,
            [ActionCreator.CreatePlanView]: this.loadCreatePlanViewAc,
            [ActionCreator.CreateTaskView]: this.loadCreateTaskViewAc,
            [ActionCreator.CreateTeamsTabView]: this.loadCreateTeamsTabViewAc,
            [ActionCreator.CopyPlanView]: this.loadCopyPlanViewAc,
            [ActionCreator.LeftNavView]: this.loadLeftNavViewAc,
            [ActionCreator.MyDayView]: this.loadMyDayViewAc,
            [ActionCreator.MyTasksView]: this.loadMyTasksViewAc,
            [ActionCreator.MyPlansView]: this.loadMyPlansViewAc,
            [ActionCreator.MyPortfoliosView]: this.loadMyPortfoliosViewAc,
            [ActionCreator.UnoShellView]: this.loadUnoShellViewAc,
            [ActionCreator.ConvertPlanView]: this.loadConvertPlanViewAc,
            [ActionCreator.SharePlanView]: this.loadSharePlanViewAc,
            [ActionCreator.PublishingLeftNavView]: this.loadPublishingLeftNavViewAc,
            [ActionCreator.SuiteNavView]: this.loadSuiteNavViewAc,
        };
        this.actionCreatorSynchronousProxyLoaders = createProxyLoaders(this.actionCreatorLoaders, this.loggers);
        this.performanceCollection = new PerformanceCollectionActionCreator(this.dispatcher, this.stores, this.modules, this.configProvider);
        this.dataPrefetch = new DataPreFetchActionCreator(this.stores, this.dispatcher, this.loggers);
        this.routing = new RoutingActionCreator(this.dispatcher, this.modules, this.stores, this.loggers);
        this.unoShell = new UnoShellActionCreator(this.dispatcher);
    }
    getActivityAcProvider() {
        return this.loadActivityAc;
    }
    getAppContextAcProvider() {
        return this.loadAppContextAc;
    }
    getBasicBucketAcProvider() {
        return this.loadBasicBucketAc;
    }
    getBasicPlanAcProvider() {
        return this.loadBasicPlanAc;
    }
    getGoalAcProvider() {
        return this.loadGoalAc;
    }
    getBasicTaskAcProvider() {
        return this.loadBasicTaskAc;
    }
    getDataPreFetchAcProvider() {
        return () => this.dataPrefetch;
    }
    getCapabilitiesAcProvider() {
        return this.loadCapabilitiesAc;
    }
    getChatAcProvider() {
        return this.loadChatAc;
    }
    getContainerAcProvider() {
        return this.loadContainerAc;
    }
    getCreateTaskFromTeamsMessageAcProvider() {
        return this.loadCreateTaskFromTeamsMessageAc;
    }
    getDeltaSyncAcProvider() {
        return this.loadDeltaSyncAc;
    }
    getDriveItemAcProvider() {
        return this.loadDriveItemAc;
    }
    getExportAcProvider() {
        return this.loadExportAc;
    }
    getExternalAgentAcProvider() {
        return this.loadExternalAgentAc;
    }
    getGroupsAcProvider() {
        return this.loadGroupsAc;
    }
    getIntelligenceAcProvider() {
        return this.loadIntelligenceAc;
    }
    getLinksAcProvider() {
        return this.loadLinksAc;
    }
    getModalAcProvider() {
        return this.loadModalAc;
    }
    getMruAcProvider() {
        return this.loadMruAc;
    }
    getNewsBarAcProvider() {
        return this.loadNewsBarAc;
    }
    getNotificationAcProvider() {
        return this.loadNotificationAc;
    }
    getOCVAcProvider() {
        return this.loadOCVAc;
    }
    getOperationTrackingAcProvider() {
        return this.loadOperationTrackingAc;
    }
    getOrgAcProvider() {
        return this.loadOrgAc;
    }
    getPerformanceCollectionAcProvider() {
        return () => this.performanceCollection;
    }
    getPublishingAcProvider() {
        return this.loadPublishingAc;
    }
    getPlanAiAcProvider() {
        return this.loadPlanAiAc;
    }
    getPlanConversionAcProvider() {
        return this.loadPlanConversionAc;
    }
    getPortfolioAcProvider() {
        return this.loadPortfolioAc;
    }
    getPremiumPlanAcProvider() {
        return this.loadPremiumPlanAc;
    }
    getResourceAcProvider() {
        return this.loadProjectHomeResourceAc;
    }
    getRecommendedPlansAcProvider() {
        return this.loadRecommendedPlansAc;
    }
    getRoutingAcProvider() {
        return () => this.routing;
    }
    getTeamsAcProvider() {
        return this.loadTeamsAc;
    }
    getTeamsTabAcProvider() {
        return this.loadTeamsTabAc;
    }
    getTenantAcProvider() {
        return this.loadTenantAc;
    }
    getUpsellAcProvider() {
        return this.loadUpsellAc;
    }
    getUserAcProvider() {
        return this.loadUserAc;
    }
    getKeyboardHandlersAcProvider() {
        return this.loadKeyboardHandlersAc;
    }
    getViewTransformsAcProvider() {
        return this.loadViewTransformsAc;
    }
    getBasicPlanViewAcProvider() {
        return this.loadBasicPlanViewAc;
    }
    getCreatePortfolioViewAcProvider() {
        return this.loadCreatePortfolioViewAc;
    }
    getCreatePlanViewAcProvider() {
        return this.loadCreatePlanViewAc;
    }
    getCopyPlanViewAcProvider() {
        return this.loadCopyPlanViewAc;
    }
    getConvertPlanViewAcProvider() {
        return this.loadConvertPlanViewAc;
    }
    getLeftNavViewAcProvider() {
        return this.loadLeftNavViewAc;
    }
    getMyDayViewAcProvider() {
        return this.loadMyDayViewAc;
    }
    getMyPlansViewAcProvider() {
        return this.loadMyPlansViewAc;
    }
    getMyTasksViewAcProvider() {
        return this.loadMyTasksViewAc;
    }
    getMyPortfoliosViewAcProvider() {
        return this.loadMyPortfoliosViewAc;
    }
    getUnoShellViewAcProvider() {
        return this.loadUnoShellViewAc;
    }
    getSharePlanViewAcProvider() {
        return this.loadSharePlanViewAc;
    }
    getCampaignCalloutViewAcProvider() {
        return this.loadCampaignCalloutViewAcProvider;
    }
    getCampaignAcProvider() {
        return this.loadCampaignAc;
    }
    getUnoShellAcProvider() {
        return () => this.unoShell;
    }
    getPublishingLeftNavViewAcProvider() {
        return this.loadPublishingLeftNavViewAc;
    }
    getPlandAiAcProvider() {
        return this.loadPlanAiAc;
    }
    getTemplatesAcProvider() {
        return this.loadTemplatesAc;
    }
    getCreateTaskViewAcProvider() {
        return this.loadCreateTaskViewAc;
    }
    getSuiteNavViewAcProvider() {
        return this.loadSuiteNavViewAc;
    }
    getCreateTeamsTabViewAcProvider() {
        return this.loadCreateTeamsTabViewAc;
    }
    load(actionCreator) {
        return this.actionCreatorSynchronousProxyLoaders[actionCreator]();
    }
    /**
     * Handle import error: dispatch action to route to the global error page
     * @param dispatcher The dispatcher the SPA uses to dispatch actions
     */
    async handleImportError(dispatcher) {
        await dispatcher.dispatchActionAsync(new DynamicImportFailureAction());
    }
    getAcBundleKey(actionCreator) {
        return `uno-${actionCreator}ac`;
    }
}
/**
 * Utility method to create proxy loaders from dynamic loaders
 * @param dynamicLoaders Dynamic loaders that load action creators async
 * @param loggers Loggers for error telemetry
 */
function createProxyLoaders(dynamicLoaders, loggers) {
    return Object.keys(dynamicLoaders).reduce((acc, componentName) => {
        acc[componentName] = () => createComponentProxy(componentName, dynamicLoaders[componentName], loggers);
        return acc;
    }, {});
}
